import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video } from '../../types/Video';
import { VideoService, VideoStats } from '../../services/videoService';
import VideoCard from '../VideoCard/VideoCard';
import './VideoSection.css';

/// 動画セクションコンポーネント - 最新動画と人気動画を表示
const VideoSection: React.FC = () => {
  const navigate = useNavigate();
  const [latestVideos, setLatestVideos] = useState<Video[]>([]);
  const [popularVideos, setPopularVideos] = useState<Video[]>([]);
  const [videoStats, setVideoStats] = useState<VideoStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'latest' | 'popular'>('latest');

  /// コンポーネント初期化時に動画データを取得
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        const [latest, popular, stats] = await Promise.all([
          VideoService.getLatestVideos(6),
          VideoService.getPopularVideos(6),
          VideoService.getVideoStats()
        ]);
        setLatestVideos(latest);
        setPopularVideos(popular);
        setVideoStats(stats);
      } catch (error) {
        console.error('動画データの取得に失敗しました:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  /// ローディング中の表示
  const renderLoadingCards = () => {
    return Array.from({ length: 6 }, (_, index) => (
      <div key={index} className="video-card loading">
        <div className="video-thumbnail loading-placeholder"></div>
        <div className="video-info">
          <div className="loading-title loading-placeholder"></div>
          <div className="loading-stats loading-placeholder"></div>
        </div>
      </div>
    ));
  };

  return (
    <section className="video-section" id="videos">
      <div className="video-section-container">
        <div className="section-header">
          <h2 className="section-title">
            <span className="title-icon">🎬</span>
            配信アーカイブ
          </h2>
          <p className="section-description">
            マール・アストレアの配信の思い出をいつでも振り返ることができます
          </p>
        </div>

        <div className="video-tabs">
          <button 
            className={`tab-button ${activeTab === 'latest' ? 'active' : ''}`}
            onClick={() => setActiveTab('latest')}
          >
            <span className="tab-icon">🕒</span>
            最新の配信
          </button>
          <button 
            className={`tab-button ${activeTab === 'popular' ? 'active' : ''}`}
            onClick={() => setActiveTab('popular')}
          >
            <span className="tab-icon">🔥</span>
            人気の配信
          </button>
        </div>

        <div className="video-grid">
          {loading ? (
            renderLoadingCards()
          ) : (
            <>
              {activeTab === 'latest' && latestVideos.map(video => (
                <VideoCard key={video.id} video={video} />
              ))}
              {activeTab === 'popular' && popularVideos.map(video => (
                <VideoCard key={video.id} video={video} />
              ))}
            </>
          )}
        </div>

        {!loading && (
          <div className="section-footer">
            <button 
              className="view-all-button"
              onClick={() => {
                navigate('/search');
              }}
            >
              すべての配信を見る
              <span className="button-arrow">→</span>
            </button>
          </div>
        )}

        {/* 統計情報 */}
        {!loading && videoStats && (
          <div className="video-stats-summary">
            <div className="stat-card">
              <div className="stat-icon">📺</div>
              <div className="stat-info">
                <span className="stat-number">{videoStats.totalVideos}</span>
                <span className="stat-label">アーカイブ動画</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-info">
                <span className="stat-number">
                  {videoStats.totalViews.toLocaleString()}
                </span>
                <span className="stat-label">総視聴回数</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">❤️</div>
              <div className="stat-info">
                <span className="stat-number">
                  {videoStats.totalLikes.toLocaleString()}
                </span>
                <span className="stat-label">総いいね数</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">⏱️</div>
              <div className="stat-info">
                <span className="stat-number">{videoStats.totalDuration}</span>
                <span className="stat-label">総配信時間</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default VideoSection; 